// src/pages/MandateDetail.js
import { useState, useRef, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import * as XLSX from 'xlsx'
import api from '../lib/api'

// ── Constants ─────────────────────────────────────────────────────────────

const DOCUMENT_TYPES = [
  'Engagement Letter', 'PPM / Offering Document', 'Due Diligence',
  'Meeting Deck', 'Investor Correspondence', 'Other',
]
const ACCEPTED_EXTENSIONS = '.pdf,.docx,.pptx,.xlsx,.doc,.xls,.ppt,.csv,.txt,.jpg,.jpeg,.png'
const MAX_FILE_BYTES = 50 * 1024 * 1024

const PIPELINE_STAGES = [
  'IDENTIFIED', 'MATERIALS SENT', 'IN DISCUSSION', 'IN DILIGENCE',
  'IN DOCUMENTATION', 'CLOSED', 'PASSED', 'UNRESPONSIVE',
]
const BD_STAGES    = ['REVIEWING', 'IN DISCUSSIONS', 'NEGOTIATING EL', 'LIVE', 'ON HOLD', 'PASSED']
const STATUSES     = ['Active', 'On Hold', 'Closed', 'Passed']
const INT_TYPES    = ['Email', 'Meeting', 'Call', 'Note', 'Event']
const INT_SOURCES  = ['Manual', 'Gmail Sync', 'Calendar Sync', 'Outlook Sync']
const DIRECTIONS   = ['Inbound', 'Outbound']

const BD_BADGE = {
  'REVIEWING': 'badge-gray', 'IN DISCUSSIONS': 'badge-blue',
  'NEGOTIATING EL': 'badge-yellow', 'LIVE': 'badge-green',
  'ON HOLD': 'badge-yellow', 'PASSED': 'badge-red',
}
const PL_BADGE = {
  'IDENTIFIED': 'badge-gray', 'MATERIALS SENT': 'badge-blue',
  'IN DISCUSSION': 'badge-blue', 'IN DILIGENCE': 'badge-yellow',
  'IN DOCUMENTATION': 'badge-purple',
  'CLOSED': 'badge-green', 'PASSED': 'badge-red', 'UNRESPONSIVE': 'badge-gray',
}
const PL_ACCENT = {
  'IDENTIFIED': '#4b5563', 'MATERIALS SENT': '#3b82f6',
  'IN DISCUSSION': '#0ea5e9', 'IN DILIGENCE': '#f59e0b',
  'IN DOCUMENTATION': '#8b5cf6',
  'CLOSED': '#10b981', 'PASSED': '#6b7280', 'UNRESPONSIVE': '#9ca3af',
}
const TYPE_BADGE = {
  'Email': 'badge-blue', 'Meeting': 'badge-purple',
  'Call': 'badge-green', 'Note': 'badge-gray', 'Event': 'badge-yellow',
}
const DOC_TYPE_BADGE = {
  'Engagement Letter': 'badge-blue', 'PPM / Offering Document': 'badge-purple',
  'Due Diligence': 'badge-yellow', 'Meeting Deck': 'badge-green',
  'Investor Correspondence': 'badge-blue', 'Other': 'badge-gray',
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatRaise(n) {
  if (n == null) return null
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `$${Math.round(n / 1e6)}M`
  return `$${Number(n).toLocaleString()}`
}
function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
function formatFileSize(bytes) {
  if (!bytes) return '—'
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.round(bytes / 1024)} KB`
}

function daysSince(iso) {
  if (!iso) return null
  const diff = Math.floor((Date.now() - new Date(iso)) / 86_400_000)
  if (diff === 0) return 'Today'
  if (diff === 1) return '1 day ago'
  return `${diff} days ago`
}

// ── StageMenu (reusable inline dropdown) ─────────────────────────────────

function StageMenu({ stages, current, onSelect, onClose, zIndex = 200 }) {
  const ref = useRef()
  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [onClose])

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 4px)', left: 0,
      zIndex, background: '#1a2235', border: '1px solid var(--border-light)',
      borderRadius: 6, padding: 4, minWidth: 180,
      boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
    }}>
      {stages.map(s => (
        <button key={s} disabled={s === current}
          onClick={e => { e.stopPropagation(); e.preventDefault(); onSelect(s) }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: s === current ? 'var(--accent-muted)' : 'transparent',
            border: 'none', borderRadius: 4, padding: '7px 10px',
            cursor: s === current ? 'default' : 'pointer',
            color: s === current ? '#60a5fa' : 'var(--text-primary)',
            fontSize: 12, fontWeight: s === current ? 600 : 400,
            fontFamily: 'inherit', textAlign: 'left',
          }}>
          <span style={{ width: 12 }}>{s === current ? '✓' : ''}</span>{s}
        </button>
      ))}
    </div>
  )
}

// ── Modal shell ───────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 520, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1300, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children}</div>
    </div>
  )
}

// ── Upload Document Modal ─────────────────────────────────────────────────

function UploadDocumentModal({ mandateId, onClose, onUploaded }) {
  const [file,         setFile]     = useState(null)
  const [docType,      setDocType]  = useState('')
  const [notes,        setNotes]    = useState('')
  const [uploading,    setUploading]= useState(false)
  const [progress,     setProgress] = useState(0)
  const [err,          setErr]      = useState(null)
  const [dragging,     setDragging] = useState(false)
  const fileRef = useRef()

  function pickFile(f) {
    if (!f) return
    if (f.size > MAX_FILE_BYTES) { setErr('File must be 50 MB or smaller.'); return }
    setFile(f); setErr(null)
  }

  function handleDrop(e) {
    e.preventDefault(); setDragging(false)
    pickFile(e.dataTransfer.files[0])
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!file) return setErr('Please select a file.')
    setUploading(true); setErr(null); setProgress(0)

    const fd = new FormData()
    fd.append('file', file)
    if (docType) fd.append('document_type', docType)
    if (notes)   fd.append('notes', notes)

    try {
      await api.post(`/api/mandates/${mandateId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ev => setProgress(Math.round((ev.loaded * 100) / ev.total)),
      })
      onUploaded()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setUploading(false)
    }
  }

  const dropZoneStyle = {
    border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border-light)'}`,
    borderRadius: 8, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
    background: dragging ? 'var(--accent-muted)' : 'var(--surface-raised)',
    transition: 'border-color 0.15s, background 0.15s',
  }

  return (
    <Modal title="Upload Document" onClose={onClose} width={480}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        {/* Drop zone */}
        <div
          style={dropZoneStyle}
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <input
            ref={fileRef} type="file" accept={ACCEPTED_EXTENSIONS}
            style={{ display: 'none' }}
            onChange={e => pickFile(e.target.files[0])}
          />
          {file ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                {file.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {formatFileSize(file.size)} · click to change
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Drag & drop a file here, or <span style={{ color: 'var(--accent)' }}>browse</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                PDF, Word, Excel, PowerPoint, CSV, TXT, image · max 50 MB
              </div>
            </div>
          )}
        </div>

        {/* Upload progress bar */}
        {uploading && (
          <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2, background: 'var(--accent)',
              width: `${progress}%`, transition: 'width 0.2s',
            }} />
          </div>
        )}

        {/* Document type */}
        <div className="input-group">
          <label className="input-label">Document Type</label>
          <select className="select" value={docType} onChange={e => setDocType(e.target.value)} disabled={uploading}>
            <option value="">Select type…</option>
            {DOCUMENT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        {/* Notes */}
        <div className="input-group">
          <label className="input-label">Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <textarea className="textarea" rows={2} value={notes} disabled={uploading}
            placeholder="Version notes, context, what this document covers…"
            onChange={e => setNotes(e.target.value)} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={uploading || !file}>
            {uploading ? `Uploading ${progress}%…` : 'Upload'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Documents Section ─────────────────────────────────────────────────────

function DocumentsSection({ mandateId }) {
  const [uploadOpen, setUploadOpen] = useState(false)
  const qc = useQueryClient()

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['documents', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/documents`).then(r => r.data.data ?? []),
  })

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/documents/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', mandateId] }),
  })

  function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return
    deleteMutation.mutate(doc.document_id)
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: docs.length > 0 ? 14 : 0 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Documents</h2>
          {!isLoading && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {docs.length} file{docs.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
          + Upload Document
        </button>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
          <div className="spinner" />
        </div>
      ) : docs.length === 0 ? (
        <div className="empty-state" style={{ padding: '28px 20px', marginTop: 14 }}>
          <h3 style={{ fontSize: 13 }}>No documents yet</h3>
          <p>Upload an engagement letter, PPM, or other mandate documents.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 160px 70px 90px 1fr 36px',
            padding: '7px 14px',
            background: 'var(--surface-raised)',
            borderBottom: '1px solid var(--border)',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)',
            gap: 12,
          }}>
            <span>File</span>
            <span>Type</span>
            <span>Size</span>
            <span>Uploaded</span>
            <span>Notes</span>
            <span />
          </div>

          {/* Rows */}
          {docs.map((doc, idx) => (
            <div key={doc.document_id} style={{
              display: 'grid',
              gridTemplateColumns: '2fr 160px 70px 90px 1fr 36px',
              padding: '10px 14px',
              borderBottom: idx < docs.length - 1 ? '1px solid var(--border)' : 'none',
              alignItems: 'center', gap: 12,
              fontSize: 13,
            }}>
              {/* File name — click to download */}
              <a
                href={doc.signed_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--accent)', fontWeight: 500, fontSize: 13,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  textDecoration: 'none',
                }}
                title={doc.file_name}
              >
                {doc.file_name}
              </a>

              {/* Document type badge */}
              <div>
                {doc.document_type
                  ? <span className={`badge ${DOC_TYPE_BADGE[doc.document_type] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>
                      {doc.document_type}
                    </span>
                  : <span style={{ color: 'var(--text-muted)' }}>—</span>
                }
              </div>

              {/* Size */}
              <span style={{ color: 'var(--text-secondary)' }}>{formatFileSize(doc.file_size)}</span>

              {/* Upload date */}
              <span style={{ color: 'var(--text-secondary)' }}>{formatDate(doc.created_at)}</span>

              {/* Notes (truncated) */}
              <span style={{
                color: 'var(--text-secondary)', fontSize: 12,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }} title={doc.notes ?? ''}>
                {doc.notes ? (doc.notes.length > 60 ? doc.notes.slice(0, 60) + '…' : doc.notes) : '—'}
              </span>

              {/* Delete */}
              <button
                className="btn-icon"
                onClick={() => handleDelete(doc)}
                disabled={deleteMutation.isPending}
                title="Delete document"
                style={{ color: 'var(--text-muted)', fontSize: 14 }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadDocumentModal
          mandateId={mandateId}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => qc.invalidateQueries({ queryKey: ['documents', mandateId] })}
        />
      )}
    </div>
  )
}

// ── Section 1: Mandate Header ─────────────────────────────────────────────

function MandateHeader({ mandate, onEditSaved }) {
  const [editOpen, setEditOpen] = useState(false)
  const qc = useQueryClient()

  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
    enabled: editOpen,
  })
  const partnerFirms = (firmsData ?? []).filter(f => f.firm_type?.includes('Partner Advisor'))

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/mandates/${mandate.mandate_id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mandate', mandate.mandate_id] })
      setEditOpen(false)
    },
  })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{mandate.mandate_name}</h1>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            {mandate.gp_firm?.name ?? '—'}
            {mandate.coverage_type === 'Partnered' && mandate.partner_firm && (
              <span style={{ color: 'var(--text-muted)', marginLeft: 10 }}>
                · Partner: {mandate.partner_firm.name}
              </span>
            )}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditOpen(true)}>
          Edit
        </button>
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className={`badge ${BD_BADGE[mandate.bd_stage] ?? 'badge-gray'}`}>{mandate.bd_stage}</span>
        <span className={`badge ${mandate.status === 'Active' ? 'badge-green' : mandate.status === 'Closed' ? 'badge-red' : 'badge-gray'}`}>
          {mandate.status}
        </span>
        <span className={`badge ${mandate.coverage_type === 'Partnered' ? 'badge-purple' : 'badge-gray'}`}>
          {mandate.coverage_type ?? 'Solo'}
        </span>
        {mandate.product_type?.map(pt => (
          <span key={pt} className="badge badge-gray" style={{ fontSize: 10, opacity: 0.75 }}>{pt}</span>
        ))}
      </div>

      {/* Detail grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px 24px' }}>
        <Field label="Target Raise">{formatRaise(mandate.target_raise) ?? '—'}</Field>
        <Field label="Engagement Letter">{mandate.engagement_letter_date ? formatDate(mandate.engagement_letter_date) : '—'}</Field>
        <Field label="Relationship Owner">{mandate.relationship_owner ? mandate.relationship_owner.slice(0, 8) + '…' : '—'}</Field>
        <Field label="Introduced Via">{mandate.introduced_via ? mandate.introduced_via.slice(0, 8) + '…' : '—'}</Field>
        {mandate.strategy_sector?.length > 0 && (
          <Field label="Strategy / Sector">
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {mandate.strategy_sector.map(t => (
                <span key={t} className="badge badge-gray" style={{ fontSize: 10 }}>{t}</span>
              ))}
            </div>
          </Field>
        )}
        {mandate.geography_focus?.length > 0 && (
          <Field label="Geography">
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {mandate.geography_focus.map(t => (
                <span key={t} className="badge badge-blue" style={{ fontSize: 10 }}>{t}</span>
              ))}
            </div>
          </Field>
        )}
      </div>

      {/* Edit modal */}
      {editOpen && (
        <EditMandateModal
          mandate={mandate}
          partnerFirms={partnerFirms}
          onClose={() => setEditOpen(false)}
          onSave={updateMutation.mutateAsync}
          saving={updateMutation.isPending}
          saveError={updateMutation.error?.response?.data?.error}
        />
      )}
    </div>
  )
}

// ── Edit Mandate Modal ────────────────────────────────────────────────────

function EditMandateModal({ mandate, partnerFirms, onClose, onSave, saving, saveError }) {
  const [form, setForm] = useState({
    bd_stage:        mandate.bd_stage,
    status:          mandate.status,
    coverage_type:   mandate.coverage_type ?? 'Solo',
    partner_firm_id: mandate.partner_firm_id ?? '',
  })
  const [err, setErr] = useState(null)

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  async function handleSubmit(e) {
    e.preventDefault()
    const payload = { ...form }
    if (payload.coverage_type !== 'Partnered') payload.partner_firm_id = null
    if (!payload.partner_firm_id) delete payload.partner_firm_id
    try { await onSave(payload) }
    catch (e) { setErr(e.response?.data?.error || e.message) }
  }

  return (
    <Modal title="Edit Mandate" onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err || saveError} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">BD Stage</label>
            <select className="select" value={form.bd_stage} onChange={e => set('bd_stage', e.target.value)}>
              {BD_STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Status</label>
            <select className="select" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Coverage Type</label>
          <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
            {['Solo', 'Partnered'].map(t => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                fontSize: 13, color: form.coverage_type === t ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: form.coverage_type === t ? 500 : 400 }}>
                <input type="radio" name="cov" value={t} checked={form.coverage_type === t}
                  onChange={() => set('coverage_type', t)} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                {t}
              </label>
            ))}
          </div>
        </div>

        {form.coverage_type === 'Partnered' && (
          <div className="input-group">
            <label className="input-label">Partner Firm</label>
            <select className="select" value={form.partner_firm_id} onChange={e => set('partner_firm_id', e.target.value)}>
              <option value="">Select partner firm…</option>
              {partnerFirms.map(f => <option key={f.firm_id} value={f.firm_id}>{f.name}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Section 2: Investor Pipeline ──────────────────────────────────────────

// ── Investor card ─────────────────────────────────────────────────────────

function InvestorCard({ entry, onClick, onDelete }) {
  const since = daysSince(entry.last_activity)
  return (
    <div onClick={onClick} style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 6, padding: '12px 13px', cursor: 'pointer',
      transition: 'border-color 0.12s, background 0.12s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-light)'; e.currentTarget.style.background = 'var(--surface-raised)' }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.background = '' }}
    >
      {/* Contact + firm */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
          {entry.investor_contact?.name ?? '—'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {entry.investor_firm?.name ?? '—'}
        </div>
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
        <span className={`badge ${PL_BADGE[entry.stage] ?? 'badge-gray'}`}>{entry.stage}</span>
        {entry.on_engagement_list && (
          <span className="badge badge-green" style={{ fontSize: 10 }}>On EL</span>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
        <div style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
          {entry.stage === 'CLOSED' && entry.actual_commitment
            ? formatRaise(entry.actual_commitment)
            : formatRaise(entry.target_commitment) ?? '—'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--text-muted)' }}>
            {since ?? (entry.partner_advisor ? `via ${entry.partner_advisor.name.split(' ')[0]}` : null)}
          </span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(entry) }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', padding: '2px 3px', borderRadius: 3,
              display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            title="Delete investor"
          ><TrashIcon /></button>
        </div>
      </div>
    </div>
  )
}

// ── Investor detail modal ─────────────────────────────────────────────────

function InvestorDetailModal({ entry, onClose, onUpdate, onLogInteraction, onDelete }) {
  const [stageOpen, setStageOpen] = useState(false)
  const [notes, setNotes] = useState(entry.notes ?? '')
  const [notesSaved, setNotesSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  // Contacts for the editable Investor Contact dropdown: the firm's own
  // contacts first, with all contacts available as a fallback (mirrors the
  // inline table cell — the firm often has no primary-firm contacts).
  const { data: allContactsData = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const { data: firmContactsData = [] } = useQuery({
    queryKey: ['contacts', { firm_id: entry.investor_firm_id }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: entry.investor_firm_id } }).then(r => r.data.data ?? []),
    enabled: !!entry.investor_firm_id,
  })

  async function handleUpdate(patch) {
    setSaving(true); setErr(null)
    try { await onUpdate(entry.pipeline_id, patch) }
    catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setSaving(false) }
  }

  async function saveNotes() {
    await handleUpdate({ notes })
    setNotesSaved(true)
    setTimeout(() => setNotesSaved(false), 2000)
  }

  return (
    <Modal title="Investor Detail" onClose={onClose} width={480}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        {/* Identity */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Investor Contact">
            <select
              className="select"
              value={entry.investor_contact_id ?? ''}
              disabled={saving}
              onChange={ev => handleUpdate({ investor_contact_id: ev.target.value || null })}
              style={{ fontSize: 13, padding: '4px 8px', maxWidth: '100%' }}
            >
              <option value="">— No contact —</option>
              {firmContactsData.length > 0 && (
                <optgroup label={`At ${entry.investor_firm?.name ?? 'this firm'}`}>
                  {firmContactsData.map(c => (
                    <option key={c.contact_id} value={c.contact_id}>
                      {c.name}{c.title_role ? ` — ${c.title_role}` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All contacts">
                {allContactsData
                  .filter(c => !firmContactsData.some(fc => fc.contact_id === c.contact_id))
                  .map(c => (
                    <option key={c.contact_id} value={c.contact_id}>
                      {c.name}{c.title_role ? ` — ${c.title_role}` : ''}
                    </option>
                  ))}
              </optgroup>
            </select>
          </Field>
          <Field label="Investor Firm">{entry.investor_firm?.name ?? '—'}</Field>
          <Field label="Partner Advisor">{entry.partner_advisor?.name ?? '—'}</Field>
          <Field label="Last Activity">{daysSince(entry.last_activity) ?? '—'}</Field>
          <Field label="Target Commitment">{formatRaise(entry.target_commitment) ?? '—'}</Field>
          {entry.actual_commitment && <Field label="Actual Commitment">{formatRaise(entry.actual_commitment)}</Field>}
        </div>

        <div className="divider" />

        {/* Stage update */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
            Pipeline Stage
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className={`badge ${PL_BADGE[entry.stage] ?? 'badge-gray'}`}
              onClick={() => setStageOpen(v => !v)}
              style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit', fontSize: 11 }}
              title="Click to change stage"
            >
              {entry.stage} ▾
            </button>
            {stageOpen && (
              <StageMenu
                stages={PIPELINE_STAGES}
                current={entry.stage}
                zIndex={1100}
                onClose={() => setStageOpen(false)}
                onSelect={s => { setStageOpen(false); handleUpdate({ stage: s }) }}
              />
            )}
          </div>
        </div>

        {/* Engagement list toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--surface-raised)', borderRadius: 6, padding: '10px 14px' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Engagement List</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              Formally placed on the engagement list submitted to the GP
            </div>
          </div>
          <button
            onClick={() => handleUpdate({ on_engagement_list: !entry.on_engagement_list })}
            disabled={saving}
            style={{
              width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
              background: entry.on_engagement_list ? 'var(--success)' : 'var(--border-light)',
              transition: 'background 0.2s', flexShrink: 0,
              position: 'relative',
            }}
          >
            <span style={{
              position: 'absolute', top: 3,
              left: entry.on_engagement_list ? 21 : 3,
              width: 16, height: 16, borderRadius: '50%',
              background: '#fff', transition: 'left 0.2s',
            }} />
          </button>
        </div>

        {/* Notes */}
        <div className="input-group">
          <label className="input-label">Notes</label>
          <textarea className="textarea" rows={3} value={notes}
            onChange={e => { setNotes(e.target.value); setNotesSaved(false) }}
            placeholder="Add notes about this investor's progress…" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={saveNotes} disabled={saving}>
              {notesSaved ? '✓ Saved' : saving ? 'Saving…' : 'Save Notes'}
            </button>
          </div>
        </div>

        <div className="divider" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={onLogInteraction}>
            + Log Interaction
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(entry)}>Delete</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ── Add investor modal ────────────────────────────────────────────────────

function AddInvestorModal({ mandateId, onClose, onAdd }) {
  const [form, setForm] = useState({
    investor_firm_id: '', investor_contact_id: '',
    stage: 'IDENTIFIED', partner_advisor_id: '',
    target_commitment: '', on_engagement_list: false,
  })
  const [submitting, setSub] = useState(false)
  const [err, setErr] = useState(null)

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: allContacts } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const { data: firmContacts } = useQuery({
    queryKey: ['contacts', { firm_id: form.investor_firm_id }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: form.investor_firm_id } }).then(r => r.data.data ?? []),
    enabled: !!form.investor_firm_id,
  })

  const lpFirms    = (firmsData ?? []).filter(f => f.firm_type?.includes('LP / Investor'))
  const advisors   = (allContacts ?? []).filter(c => c.contact_type?.includes('Partner Advisor'))
  const invContacts = form.investor_firm_id ? (firmContacts ?? []) : []

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.investor_firm_id) return setErr('Investor firm is required.')
    setSub(true); setErr(null)
    const payload = {
      stage: form.stage,
      investor_firm_id: form.investor_firm_id,
      on_engagement_list: form.on_engagement_list,
    }
    if (form.investor_contact_id) payload.investor_contact_id = form.investor_contact_id
    if (form.partner_advisor_id)  payload.partner_advisor_id  = form.partner_advisor_id
    if (form.target_commitment)   payload.target_commitment   = parseFloat(form.target_commitment)
    try { await onAdd(payload); onClose() }
    catch (e) { setErr(e.response?.data?.error || e.message) }
    finally { setSub(false) }
  }

  return (
    <Modal title="Add Investor to Pipeline" onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        <div className="input-group">
          <label className="input-label">Investor Firm *</label>
          <select className="select" value={form.investor_firm_id}
            onChange={e => { set('investor_firm_id', e.target.value); set('investor_contact_id', '') }}>
            <option value="">Select LP / Investor…</option>
            {lpFirms.map(f => <option key={f.firm_id} value={f.firm_id}>{f.name}</option>)}
          </select>
          {lpFirms.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              No LP / Investor firms found. Add firms with that type first.
            </div>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">Investor Contact</label>
          <select className="select" value={form.investor_contact_id}
            onChange={e => set('investor_contact_id', e.target.value)}
            disabled={!form.investor_firm_id}>
            <option value="">{form.investor_firm_id ? 'Select contact…' : 'Select a firm first'}</option>
            {invContacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}{c.title_role ? ` — ${c.title_role}` : ''}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Stage</label>
            <select className="select" value={form.stage} onChange={e => set('stage', e.target.value)}>
              {PIPELINE_STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Target Commitment ($)</label>
            <input className="input" type="number" min="0" step="1000000"
              value={form.target_commitment} placeholder="e.g. 25000000"
              onChange={e => set('target_commitment', e.target.value)} />
            {form.target_commitment !== '' && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                = {formatRaise(parseFloat(form.target_commitment))}
              </div>
            )}
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Partner Advisor</label>
          <select className="select" value={form.partner_advisor_id}
            onChange={e => set('partner_advisor_id', e.target.value)}>
            <option value="">None</option>
            {advisors.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--surface-raised)', borderRadius: 6, padding: '10px 14px' }}>
          <label style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer' }} htmlFor="ael">
            Add to Engagement List
          </label>
          <input id="ael" type="checkbox" checked={form.on_engagement_list}
            onChange={e => set('on_engagement_list', e.target.checked)}
            style={{ accentColor: 'var(--accent)', width: 16, height: 16, cursor: 'pointer' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add Investor'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Export helper ─────────────────────────────────────────────────────────

function todayStr() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '')
}

function exportPipeline(entries, format) {
  const data = entries.map(e => ({
    Firm: e.investor_firm?.name ?? '',
    Contact: e.investor_contact?.name ?? '',
    'Contact Email': e.investor_contact?.email ?? '',
    Stage: e.stage ?? '',
    'On Engagement List': e.on_engagement_list ? 'Yes' : 'No',
    'Partner Advisor': e.partner_advisor?.name ?? '',
    'Target Commitment': e.target_commitment ? formatRaise(e.target_commitment) : '',
    Notes: e.notes ?? '',
  }))
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Pipeline')
  const filename = `RGA_Pipeline_${todayStr()}.${format === 'xlsx' ? 'xlsx' : 'csv'}`
  if (format === 'xlsx') {
    XLSX.writeFile(wb, filename)
  } else {
    XLSX.writeFile(wb, filename, { bookType: 'csv' })
  }
}

// ── Pipeline Table View ───────────────────────────────────────────────────

// Ordered list of columns that support Tab-navigation
const EDITABLE_COLS = ['firm', 'contact', 'owner', 'target', 'notes']

function PipelineTableView({ entries, mandate, onUpdate, onDelete }) {
  const [search, setSearch]           = useState('')
  const [sortCol, setSortCol]         = useState('firm')
  const [sortDir, setSortDir]         = useState('asc')
  const [openStageId, setOpenStageId] = useState(null)
  // editCell: { rowId, col } | null
  const [editCell, setEditCell]       = useState(null)
  const [editValue, setEditValue]     = useState('')
  const [savingCells, setSavingCells] = useState(new Set())

  // ── Data for dropdowns ───────────────────────────────────────────────
  const { data: allFirms = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const lpFirms = allFirms.filter(f => f.firm_type?.includes('LP / Investor'))

  const { data: allContacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const partnerAdvisors = allContacts.filter(c => c.contact_type?.includes('Partner Advisor'))

  // Only fetch contacts when a contact cell is actively being edited
  const editingEntry     = editCell ? entries.find(e => e.pipeline_id === editCell.rowId) : null
  const contactFirmId    = editCell?.col === 'contact' ? (editingEntry?.investor_firm_id ?? null) : null
  const { data: firmContacts = [] } = useQuery({
    queryKey: ['contacts', { firm_id: contactFirmId }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: contactFirmId } }).then(r => r.data.data ?? []),
    enabled: !!contactFirmId,
  })

  // ── Edit helpers ─────────────────────────────────────────────────────

  // Open a cell for editing. Saves the previous cell first (fire-and-forget).
  function startEdit(rowId, col, currentValue) {
    if (editCell?.rowId === rowId && editCell?.col === col) return
    if (editCell) saveEdit(editCell.rowId, editCell.col, editValue)
    setEditCell({ rowId, col })
    setEditValue(String(currentValue ?? ''))
  }

  // Persist a cell value via PATCH, with optimistic save indicator.
  async function saveEdit(rowId, col, value) {
    const entry = entries.find(e => e.pipeline_id === rowId)
    if (!entry) return

    let patch
    if (col === 'firm') {
      if (value === (entry.investor_firm_id ?? '')) return
      // Changing firm resets contact
      patch = { investor_firm_id: value || null, investor_contact_id: null }
    } else if (col === 'contact') {
      if (value === (entry.investor_contact_id ?? '')) return
      patch = { investor_contact_id: value || null }
    } else if (col === 'target') {
      const num = value === '' ? null : parseFloat(value)
      if (num === (entry.target_commitment ?? null)) return
      patch = { target_commitment: num }
    } else if (col === 'owner') {
      if (value === (entry.partner_advisor_id ?? '')) return
      patch = { partner_advisor_id: value || null }
    } else if (col === 'notes') {
      const trimmed = value.trim()
      if (trimmed === (entry.notes ?? '')) return
      patch = { notes: trimmed }
    }
    if (!patch) return

    const key = `${rowId}-${col}`
    setSavingCells(s => new Set([...s, key]))
    try {
      await onUpdate(rowId, patch)
    } finally {
      setSavingCells(s => { const n = new Set(s); n.delete(key); return n })
    }
  }

  function commitEdit() {
    if (!editCell) return
    const { rowId, col } = editCell
    setEditCell(null)
    saveEdit(rowId, col, editValue)
  }

  function cancelEdit() {
    setEditCell(null)
    setEditValue('')
  }

  // Keyboard handler for text/number inputs
  function handleInputKeyDown(e, rowId, col) {
    if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); return }
    if (e.key === 'Enter')  { e.preventDefault(); commitEdit(); return }
    if (e.key === 'Tab') {
      e.preventDefault()
      const entry = entries.find(en => en.pipeline_id === rowId)
      const colIdx = EDITABLE_COLS.indexOf(col)
      // Save current cell
      const val = editValue
      setEditCell(null)
      saveEdit(rowId, col, val)
      // Open next editable cell in same row
      if (colIdx < EDITABLE_COLS.length - 1) {
        const nextCol = EDITABLE_COLS[colIdx + 1]
        const nextVal =
          nextCol === 'firm'    ? (entry?.investor_firm_id ?? '') :
          nextCol === 'contact' ? (entry?.investor_contact_id ?? '') :
          nextCol === 'owner'   ? (entry?.partner_advisor_id ?? '') :
          nextCol === 'target'  ? (entry?.target_commitment != null ? String(entry.target_commitment) : '') :
                                  (entry?.notes ?? '')
        setEditCell({ rowId, col: nextCol })
        setEditValue(String(nextVal))
      }
    }
  }

  // ── Sorting / filtering ──────────────────────────────────────────────

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const filtered = entries.filter(e => {
    const q = search.toLowerCase()
    if (!q) return true
    return (
      (e.investor_firm?.name    ?? '').toLowerCase().includes(q) ||
      (e.investor_contact?.name ?? '').toLowerCase().includes(q)
    )
  })

  const sorted = [...filtered].sort((a, b) => {
    let av, bv
    if (sortCol === 'firm')    { av = a.investor_firm?.name    ?? ''; bv = b.investor_firm?.name    ?? '' }
    if (sortCol === 'contact') { av = a.investor_contact?.name ?? ''; bv = b.investor_contact?.name ?? '' }
    if (sortCol === 'stage')   { av = a.stage ?? '';                  bv = b.stage ?? '' }
    if (sortCol === 'owner')   { av = a.partner_advisor?.name ?? ''; bv = b.partner_advisor?.name ?? '' }
    if (sortCol === 'target')  { av = a.target_commitment ?? 0;       bv = b.target_commitment ?? 0 }
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
    return sortDir === 'asc' ? av - bv : bv - av
  })

  const totalCommitment = filtered.reduce((sum, e) => sum + (e.target_commitment ?? 0), 0)

  // ── Shared styles ────────────────────────────────────────────────────

  const thStyle = (col) => ({
    padding: '8px 12px', fontSize: 11, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    color: 'var(--text-secondary)', cursor: col ? 'pointer' : 'default',
    whiteSpace: 'nowrap', userSelect: 'none', textAlign: 'left',
  })

  const SortArrow = ({ col }) => {
    if (sortCol !== col) return <span style={{ color: 'var(--text-muted)', marginLeft: 3 }}>↕</span>
    return <span style={{ color: 'var(--accent)', marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
  }

  // td style: highlight when this cell is being edited
  function tdStyle(rowId, col, base = {}) {
    const active = editCell?.rowId === rowId && editCell?.col === col
    return {
      padding: active ? '6px 8px' : '10px 12px',
      cursor: 'pointer',
      outline: active ? '2px solid var(--accent)' : 'none',
      outlineOffset: active ? '-2px' : undefined,
      borderRadius: active ? 4 : undefined,
      background: active ? 'rgba(59,130,246,0.07)' : 'transparent',
      transition: 'outline 0.1s, background 0.1s',
      ...base,
    }
  }

  // Shared style for inline inputs / selects
  const editInputStyle = {
    background: 'transparent', border: 'none', outline: 'none',
    color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit',
    width: '100%', padding: 0, minWidth: 80,
  }

  // Tiny spinner shown in cell while saving
  function CellSpinner() {
    return <span style={{
      display: 'inline-block', width: 10, height: 10, marginLeft: 6,
      border: '1.5px solid var(--border-light)', borderTopColor: 'var(--accent)',
      borderRadius: '50%', animation: 'spin 0.6s linear infinite', verticalAlign: 'middle',
    }} />
  }

  // ── Cell renderers ───────────────────────────────────────────────────

  function FirmCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'firm'
    const isSaving  = savingCells.has(`${e.pipeline_id}-firm`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'firm', { fontWeight: 500, whiteSpace: 'nowrap' })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'firm', e.investor_firm_id ?? '')}
        title={isEditing ? undefined : 'Click to edit firm'}
      >
        {isEditing ? (
          <select
            autoFocus
            value={editValue}
            style={editInputStyle}
            onChange={ev => {
              const val = ev.target.value
              setEditCell(null)
              saveEdit(e.pipeline_id, 'firm', val)
            }}
            onBlur={() => { if (editCell?.rowId === e.pipeline_id && editCell?.col === 'firm') commitEdit() }}
            onKeyDown={ev => { if (ev.key === 'Escape') cancelEdit() }}
          >
            <option value="">— No firm —</option>
            {lpFirms.map(f => <option key={f.firm_id} value={f.firm_id}>{f.name}</option>)}
          </select>
        ) : (
          <>
            {e.investor_firm?.name ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  function ContactCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'contact'
    const isSaving  = savingCells.has(`${e.pipeline_id}-contact`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'contact', { whiteSpace: 'nowrap', color: 'var(--text-secondary)' })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'contact', e.investor_contact_id ?? '')}
        title={isEditing ? undefined : 'Click to edit contact'}
      >
        {isEditing ? (() => {
          // Firm's own contacts first (priority), then everyone else.
          const firmIds = new Set(firmContacts.map(c => c.contact_id))
          const ordered = [
            ...firmContacts,
            ...allContacts.filter(c => !firmIds.has(c.contact_id)),
          ]
          return (
            <ContactTypeahead
              contacts={ordered}
              priorityIds={firmIds}
              placeholder="Search contacts…"
              clearLabel="— Clear contact —"
              onSave={id => { setEditCell(null); saveEdit(e.pipeline_id, 'contact', id ?? '') }}
              onCancel={cancelEdit}
            />
          )
        })() : (
          <>
            {e.investor_contact?.name
              ? <span>{e.investor_contact.name}</span>
              : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>+ Add contact</span>}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  // Shared searchable typeahead for any contact cell (partner advisor or
  // investor contact). Self-contained: owns its query text + keyboard
  // highlight, commits via onSave(id|null), cancels on blur.
  //
  // Props:
  //   contacts    — array to search (pre-sorted; priority items first)
  //   priorityIds — optional Set of contact_ids to show above a divider
  //   onSave(id)  — commit callback (null = clear)
  //   onCancel()  — cancel callback (blur / Escape)
  //   placeholder, clearLabel — display strings
  function ContactTypeahead({ contacts, priorityIds = null, onSave, onCancel, placeholder, clearLabel }) {
    const [query, setQuery]         = useState('')
    const [highlight, setHighlight] = useState(0)
    const inputRef = useRef(null)

    useEffect(() => { inputRef.current?.focus() }, [])

    const q = query.trim().toLowerCase()
    const matches = q
      ? contacts.filter(c => c.name.toLowerCase().includes(q))
      : contacts
    // Row 0 is always the "Clear" option; contact rows start at index 1.
    const rowCount = matches.length + 1
    // Leading matches that are "priority" (firm contacts) — used for divider.
    const priorityCount = priorityIds
      ? matches.filter(c => priorityIds.has(c.contact_id)).length
      : 0

    function choose(idx) {
      if (idx === 0) return onSave(null)         // clear
      const c = matches[idx - 1]
      if (c) onSave(c.contact_id)
    }

    function onKeyDown(ev) {
      if (ev.key === 'Escape')         { ev.preventDefault(); onCancel() }
      else if (ev.key === 'ArrowDown') { ev.preventDefault(); setHighlight(h => Math.min(rowCount - 1, h + 1)) }
      else if (ev.key === 'ArrowUp')   { ev.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
      else if (ev.key === 'Enter')     { ev.preventDefault(); choose(highlight) }
    }

    const rowStyle = (active) => ({
      padding: '6px 10px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
      background: active ? 'var(--accent-muted)' : 'transparent',
    })

    return (
      <div style={{ position: 'relative', minWidth: 180 }} onClick={ev => ev.stopPropagation()}>
        <input
          ref={inputRef}
          value={query}
          placeholder={placeholder}
          style={editInputStyle}
          onChange={ev => { setQuery(ev.target.value); setHighlight(0) }}
          onKeyDown={onKeyDown}
          // Blur cancels (no save) — runs after any result mousedown has fired.
          onBlur={() => setTimeout(() => onCancel(), 120)}
        />
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 30, marginTop: 4,
          minWidth: 220, maxHeight: 6 * 34, overflowY: 'auto',
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {/* Clear option (row 0) */}
          <div
            style={{ ...rowStyle(highlight === 0), color: 'var(--text-muted)', fontStyle: 'italic' }}
            onMouseEnter={() => setHighlight(0)}
            onMouseDown={ev => { ev.preventDefault(); choose(0) }}
          >
            {clearLabel}
          </div>
          {matches.length === 0 ? (
            <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
              No matches
            </div>
          ) : matches.map((c, i) => {
            // Subtle divider before the first non-priority (non-firm) contact
            const isFirstOther = priorityCount > 0 && i === priorityCount
            return (
              <div
                key={c.contact_id}
                style={{ ...rowStyle(highlight === i + 1), borderTop: isFirstOther ? '1px solid var(--border)' : undefined }}
                onMouseEnter={() => setHighlight(i + 1)}
                onMouseDown={ev => { ev.preventDefault(); choose(i + 1) }}
              >
                <span style={{ color: 'var(--text-primary)' }}>{c.name}</span>
                {c.primary_firm?.name && (
                  <span style={{ color: 'var(--text-muted)' }}> · {c.primary_firm.name}</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  function OwnerCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'owner'
    const isSaving  = savingCells.has(`${e.pipeline_id}-owner`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'owner', { whiteSpace: 'nowrap' })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'owner', e.partner_advisor_id ?? '')}
        title={isEditing ? undefined : 'Click to edit partner advisor'}
      >
        {isEditing ? (
          <ContactTypeahead
            contacts={partnerAdvisors}
            placeholder="Search advisors…"
            clearLabel="— Clear advisor —"
            onSave={id => { setEditCell(null); saveEdit(e.pipeline_id, 'owner', id ?? '') }}
            onCancel={cancelEdit}
          />
        ) : (
          <>
            {e.partner_advisor?.name
              ? <span style={{ color: 'var(--text-secondary)' }}>{e.partner_advisor.name}</span>
              : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>+ Add advisor</span>}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  function TargetCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'target'
    const isSaving  = savingCells.has(`${e.pipeline_id}-target`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'target', { whiteSpace: 'nowrap', fontWeight: 500 })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'target',
          e.target_commitment != null ? String(e.target_commitment) : '')}
        title={isEditing ? undefined : 'Click to edit target commitment'}
      >
        {isEditing ? (
          <input
            autoFocus
            type="number"
            min="0"
            step="1000000"
            value={editValue}
            style={{ ...editInputStyle, width: 120 }}
            onChange={ev => setEditValue(ev.target.value)}
            onBlur={commitEdit}
            onKeyDown={ev => handleInputKeyDown(ev, e.pipeline_id, 'target')}
            placeholder="e.g. 25000000"
          />
        ) : (
          <>
            <span style={{ color: 'var(--text-secondary)' }}>
              {e.target_commitment ? formatRaise(e.target_commitment) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
            </span>
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  function NotesCell({ e }) {
    const isEditing = editCell?.rowId === e.pipeline_id && editCell?.col === 'notes'
    const isSaving  = savingCells.has(`${e.pipeline_id}-notes`)
    return (
      <td
        style={tdStyle(e.pipeline_id, 'notes', { maxWidth: 220 })}
        onClick={() => !isEditing && startEdit(e.pipeline_id, 'notes', e.notes ?? '')}
        title={isEditing ? undefined : (e.notes ?? 'Click to add notes')}
      >
        {isEditing ? (
          <input
            autoFocus
            type="text"
            value={editValue}
            style={{ ...editInputStyle, width: '100%', minWidth: 160 }}
            onChange={ev => setEditValue(ev.target.value)}
            onBlur={commitEdit}
            onKeyDown={ev => handleInputKeyDown(ev, e.pipeline_id, 'notes')}
            placeholder="Add notes…"
          />
        ) : (
          <>
            {e.notes
              ? <span style={{ color: 'var(--text-secondary)', fontSize: 12,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 200 }}>
                  {e.notes.length > 60 ? e.notes.slice(0, 60) + '…' : e.notes}
                </span>
              : <span style={{ color: 'var(--text-muted)' }}>—</span>}
            {isSaving && <CellSpinner />}
          </>
        )}
      </td>
    )
  }

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="input"
          placeholder="Search firm or contact…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 240, fontSize: 13 }}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => exportPipeline(sorted, 'csv')}>Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportPipeline(sorted, 'xlsx')}>Export Excel</button>
        </div>
      </div>

      {/* Table */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle('firm')}    onClick={() => toggleSort('firm')}>Firm <SortArrow col="firm" /></th>
              <th style={thStyle('contact')} onClick={() => toggleSort('contact')}>Contact <SortArrow col="contact" /></th>
              <th style={thStyle(null)}>Email</th>
              <th style={thStyle('stage')}   onClick={() => toggleSort('stage')}>Stage <SortArrow col="stage" /></th>
              <th style={thStyle(null)}>On EL</th>
              <th style={thStyle('owner')}   onClick={() => toggleSort('owner')}>Partner Advisor <SortArrow col="owner" /></th>
              <th style={thStyle('target')}  onClick={() => toggleSort('target')}>Target <SortArrow col="target" /></th>
              <th style={thStyle(null)}>Notes</th>
              <th style={thStyle(null)}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  {search ? 'No results match your search.' : 'No investors in pipeline.'}
                </td>
              </tr>
            )}
            {sorted.map((e, idx) => (
              <tr key={e.pipeline_id} style={{
                borderBottom: idx < sorted.length - 1 ? '1px solid var(--border)' : 'none',
                background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
              }}>

                {/* Firm — editable dropdown */}
                <FirmCell e={e} />

                {/* Contact — editable dropdown (filtered by firm) */}
                <ContactCell e={e} />

                {/* Email — read-only */}
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  {e.investor_contact?.email
                    ? <a href={`mailto:${e.investor_contact.email}`}
                        style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: 12 }}>
                        {e.investor_contact.email}
                      </a>
                    : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>

                {/* Stage — existing badge dropdown */}
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      className={`badge ${PL_BADGE[e.stage] ?? 'badge-gray'}`}
                      onClick={() => setOpenStageId(v => v === e.pipeline_id ? null : e.pipeline_id)}
                      style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit', fontSize: 10, whiteSpace: 'nowrap' }}
                      title="Click to change stage"
                    >
                      {e.stage} ▾
                    </button>
                    {openStageId === e.pipeline_id && (
                      <StageMenu
                        stages={PIPELINE_STAGES}
                        current={e.stage}
                        zIndex={300}
                        onClose={() => setOpenStageId(null)}
                        onSelect={s => { setOpenStageId(null); onUpdate(e.pipeline_id, { stage: s }) }}
                      />
                    )}
                  </div>
                </td>

                {/* On Engagement List — toggle */}
                <td style={{ padding: '10px 12px' }}>
                  <button
                    onClick={() => onUpdate(e.pipeline_id, { on_engagement_list: !e.on_engagement_list })}
                    style={{
                      width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: e.on_engagement_list ? 'var(--success)' : 'var(--border-light)',
                      transition: 'background 0.2s', position: 'relative', flexShrink: 0,
                    }}
                    title={e.on_engagement_list ? 'On engagement list — click to remove' : 'Not on engagement list — click to add'}
                  >
                    <span style={{
                      position: 'absolute', top: 2,
                      left: e.on_engagement_list ? 18 : 2,
                      width: 16, height: 16, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s',
                    }} />
                  </button>
                </td>

                {/* Owner — editable user dropdown */}
                <OwnerCell e={e} />

                {/* Target Commitment — editable number */}
                <TargetCell e={e} />

                {/* Notes — editable text */}
                <NotesCell e={e} />

                {/* Delete */}
                <td style={{ padding: '10px 8px' }}>
                  <button
                    onClick={() => onDelete(e)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 3,
                      display: 'inline-flex', alignItems: 'center' }}
                    onMouseEnter={ev => ev.currentTarget.style.color = 'var(--danger)'}
                    onMouseLeave={ev => ev.currentTarget.style.color = 'var(--text-muted)'}
                    title="Delete investor"
                  >
                    <TrashIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
        <span>
          {filtered.length} investor{filtered.length !== 1 ? 's' : ''}
          <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 11 }}>
            · Click any cell in Firm, Contact, Partner Advisor, Target, or Notes to edit inline
          </span>
        </span>
        {totalCommitment > 0 && (
          <span>Total target: <strong style={{ color: 'var(--text-primary)' }}>{formatRaise(totalCommitment)}</strong></span>
        )}
      </div>
    </div>
  )
}

// ── Pipeline Kanban ───────────────────────────────────────────────────────

function PipelineKanban({ entries, mandateId, mandate, onEntryUpdate, onEntryAdded }) {
  const [addOpen, setAddOpen]       = useState(false)
  const [detailEntry, setDetail]    = useState(null)
  const [logFromEntry, setLogEntry] = useState(null) // open log modal from inside detail modal
  const [toDelete, setToDelete]     = useState(null)
  const [pipelineView, setPipelineView] = useState('board')
  const qc = useQueryClient()

  const addMutation = useMutation({
    mutationFn: payload => api.post(`/api/mandates/${mandateId}/pipeline`, payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pipeline', mandateId] }); onEntryAdded?.() },
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/api/pipeline/${id}`, patch).then(r => r.data),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['pipeline', mandateId] })
      const previous = qc.getQueryData(['pipeline', mandateId])
      qc.setQueryData(['pipeline', mandateId], old =>
        (old ?? []).map(e => e.pipeline_id === id ? { ...e, ...patch } : e)
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['pipeline', mandateId], ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['pipeline', mandateId] }),
    onSuccess: d => {
      setDetail(prev => prev ? { ...prev, ...d.data } : null)
    },
  })

  function handleDragEnd(result) {
    const { destination, source, draggableId } = result
    if (!destination) return
    if (destination.droppableId === source.droppableId && destination.index === source.index) return
    updateMutation.mutate({ id: draggableId, patch: { stage: destination.droppableId } })
  }
  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/pipeline/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['pipeline', mandateId] })
      setToDelete(null)
      setDetail(prev => prev?.pipeline_id === id ? null : prev)
    },
  })

  const grouped = Object.fromEntries(
    PIPELINE_STAGES.map(s => [s, entries.filter(e => e.stage === s)])
  )
  const total = entries.length

  return (
    <div>
      {/* Sub-header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Investor Pipeline</h2>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {total} investor{total !== 1 ? 's' : ''} tracked
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className={`btn btn-sm ${pipelineView === 'board' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPipelineView('board')}
          >Board</button>
          <button
            className={`btn btn-sm ${pipelineView === 'table' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPipelineView('table')}
          >Table</button>
          <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>
            + Add Investor
          </button>
        </div>
      </div>

      {/* Table view */}
      {pipelineView === 'table' && (
        <PipelineTableView
          entries={entries}
          mandate={mandate}
          onUpdate={(id, patch) => updateMutation.mutateAsync({ id, patch })}
          onDelete={e => setToDelete(e)}
        />
      )}

      {/* Kanban scroll container */}
      {pipelineView === 'board' && (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div style={{
          display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 12,
          alignItems: 'flex-start',
          scrollbarWidth: 'thin', scrollbarColor: 'var(--border-light) transparent',
        }}>

          {PIPELINE_STAGES.map(stage => (
            <div key={stage} style={{ minWidth: 200, width: 200, flexShrink: 0 }}>
              {/* Column header */}
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderBottom: 'none', borderRadius: '6px 6px 0 0',
                borderTop: `3px solid ${PL_ACCENT[stage]}`,
                padding: '8px 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                  {stage}
                </span>
                <span className="badge badge-gray" style={{ fontSize: 10, minWidth: 18, justifyContent: 'center' }}>
                  {grouped[stage].length}
                </span>
              </div>
              {/* Cards with drop zone */}
              <Droppable droppableId={stage}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      background: snapshot.isDraggingOver ? '#111d30' : '#0d1321',
                      border: '1px solid var(--border)',
                      borderTop: 'none', borderRadius: '0 0 6px 6px',
                      padding: 7, display: 'flex', flexDirection: 'column', gap: 6,
                      minHeight: 100,
                      transition: 'background 0.15s',
                    }}
                  >
                    {grouped[stage].length === 0 && !snapshot.isDraggingOver && (
                      <div style={{ padding: '14px 8px', textAlign: 'center',
                        fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        None
                      </div>
                    )}
                    {grouped[stage].map((e, index) => (
                      <Draggable key={e.pipeline_id} draggableId={e.pipeline_id} index={index}>
                        {(drag, dragSnapshot) => (
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            {...drag.dragHandleProps}
                            style={{
                              borderRadius: 6,
                              opacity: dragSnapshot.isDragging ? 0.92 : 1,
                              boxShadow: dragSnapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.5)' : 'none',
                              ...drag.draggableProps.style,
                            }}
                          >
                            <InvestorCard
                              entry={e}
                              onClick={() => setDetail(e)}
                              onDelete={e => setToDelete(e)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
      )}

      {/* Add investor modal */}
      {addOpen && (
        <AddInvestorModal
          mandateId={mandateId}
          onClose={() => setAddOpen(false)}
          onAdd={addMutation.mutateAsync}
        />
      )}

      {/* Investor detail modal */}
      {detailEntry && !logFromEntry && (
        <InvestorDetailModal
          entry={detailEntry}
          onClose={() => setDetail(null)}
          onUpdate={(id, patch) => updateMutation.mutateAsync({ id, patch })}
          onLogInteraction={() => { setLogEntry(detailEntry); setDetail(null) }}
          onDelete={e => { setDetail(null); setToDelete(e) }}
        />
      )}

      {/* Log interaction modal — triggered from investor detail */}
      {logFromEntry && (
        <LogInteractionModal
          mandateId={mandateId}
          onClose={() => setLogEntry(null)}
          zIndex={1200}
        />
      )}

      {/* Delete confirm */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.investor_contact?.name ?? toDelete.investor_firm?.name ?? 'this investor'}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.pipeline_id)}
          deleting={deleteMutation.isPending}
        />
      )}
    </div>
  )
}

// ── Section 3: Log Interaction Modal ─────────────────────────────────────

function LogInteractionModal({ mandateId, onClose, onCreated, zIndex = 1000 }) {
  const [form, setForm] = useState({
    type: 'Meeting', direction: 'Outbound', source: 'Manual',
    subject_title: '', body_notes: '',
    date: new Date().toISOString().slice(0, 16),
    follow_up_due: '', contact_ids: [],
  })
  const [submitting, setSub] = useState(false)
  const [err, setErr] = useState(null)
  const [search, setSearch] = useState('')

  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const allContacts = contactsData ?? []

  const qc = useQueryClient()

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleContact(id) {
    setForm(p => ({
      ...p,
      contact_ids: p.contact_ids.includes(id)
        ? p.contact_ids.filter(c => c !== id)
        : [...p.contact_ids, id],
    }))
  }

  const filteredContacts = allContacts.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  )

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.type)   return setErr('Type is required.')
    if (!form.date)   return setErr('Date is required.')
    setSub(true); setErr(null)

    const payload = {
      type: form.type, direction: form.direction || undefined,
      source: form.source, date: new Date(form.date).toISOString(),
      mandate_id: mandateId,
    }
    if (form.subject_title) payload.subject_title = form.subject_title
    if (form.body_notes)    payload.body_notes    = form.body_notes
    if (form.follow_up_due) payload.follow_up_due = form.follow_up_due

    try {
      const res = await api.post('/api/interactions', payload)
      const newId = res.data.data?.interaction_id

      // Link each selected contact
      if (newId && form.contact_ids.length > 0) {
        await Promise.all(
          form.contact_ids.map(cid =>
            api.post(`/api/interactions/${newId}/contacts/${cid}`)
          )
        )
      }

      qc.invalidateQueries({ queryKey: ['interactions', { mandate_id: mandateId }] })
      onCreated?.()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setSub(false)
    }
  }

  return (
    <Modal title="Log Interaction" onClose={onClose} width={540} zIndex={zIndex}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ErrorBanner msg={err} />

        {/* Type + Direction */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Type *</label>
            <select className="select" value={form.type} onChange={e => set('type', e.target.value)}>
              {INT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Direction</label>
            <select className="select" value={form.direction} onChange={e => set('direction', e.target.value)}>
              <option value="">None</option>
              {DIRECTIONS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* Subject */}
        <div className="input-group">
          <label className="input-label">Subject / Title</label>
          <input className="input" value={form.subject_title} placeholder="e.g. Intro call with LP team"
            onChange={e => set('subject_title', e.target.value)} />
        </div>

        {/* Notes */}
        <div className="input-group">
          <label className="input-label">Notes</label>
          <textarea className="textarea" rows={3} value={form.body_notes}
            placeholder="Key discussion points, next steps…"
            onChange={e => set('body_notes', e.target.value)} />
        </div>

        {/* Date + Source */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Date *</label>
            <input className="input" type="datetime-local" value={form.date}
              onChange={e => set('date', e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Source</label>
            <select className="select" value={form.source} onChange={e => set('source', e.target.value)}>
              {INT_SOURCES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Follow-up due */}
        <div className="input-group">
          <label className="input-label">Follow-up Due</label>
          <input className="input" type="date" value={form.follow_up_due}
            onChange={e => set('follow_up_due', e.target.value)} />
        </div>

        {/* Contacts multi-select */}
        <div className="input-group">
          <label className="input-label">
            Contacts{form.contact_ids.length > 0 && ` (${form.contact_ids.length} selected)`}
          </label>
          <input className="input" placeholder="Search contacts…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginBottom: 6 }} />
          <div style={{
            border: '1px solid var(--border)', borderRadius: 6,
            maxHeight: 160, overflow: 'auto', background: 'var(--surface-raised)',
          }}>
            {filteredContacts.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                No contacts found
              </div>
            ) : (
              filteredContacts.map(c => {
                const checked = form.contact_ids.includes(c.contact_id)
                return (
                  <label key={c.contact_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: checked ? 'var(--accent-muted)' : 'transparent',
                    transition: 'background 0.1s',
                  }}>
                    <input type="checkbox" checked={checked}
                      onChange={() => toggleContact(c.contact_id)}
                      style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: checked ? 500 : 400,
                        color: 'var(--text-primary)' }}>{c.name}</div>
                      {c.title_role && (
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{c.title_role}</div>
                      )}
                    </div>
                  </label>
                )
              })
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Logging…' : 'Log Interaction'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Activity Feed ─────────────────────────────────────────────────────────

function ActivityFeed({ interactions, isLoading, mandateId }) {
  const [logOpen, setLogOpen] = useState(false)
  const qc = useQueryClient()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Activity Feed</h2>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {isLoading ? '…' : `${interactions.length} interaction${interactions.length !== 1 ? 's' : ''}`}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setLogOpen(true)}>
          + Log
        </button>
      </div>

      {/* List */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, flex: 1, overflow: 'auto',
      }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        ) : interactions.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 20px' }}>
            <h3 style={{ fontSize: 13 }}>No interactions yet</h3>
            <p>Log the first touchpoint for this mandate.</p>
          </div>
        ) : (
          interactions.map((i, idx) => (
            <div key={i.interaction_id} style={{
              padding: '12px 16px',
              borderBottom: idx < interactions.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span className={`badge ${TYPE_BADGE[i.type] ?? 'badge-gray'}`} style={{ flexShrink: 0, marginTop: 1 }}>
                  {i.type}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {i.subject_title || '(no subject)'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 3, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDateTime(i.date)}</span>
                    {i.direction && (
                      <span className="badge badge-gray" style={{ fontSize: 9, opacity: 0.7 }}>{i.direction}</span>
                    )}
                    {i.follow_up_due && (
                      <span style={{ fontSize: 11, color: 'var(--warning)' }}>
                        ↩ {formatDate(i.follow_up_due)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {logOpen && (
        <LogInteractionModal
          mandateId={mandateId}
          onClose={() => setLogOpen(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['interactions', { mandate_id: mandateId }] })}
        />
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function MandateDetail() {
  const { id } = useParams()

  const { data: mandateData, isLoading: mandateLoading, error: mandateError } = useQuery({
    queryKey: ['mandate', id],
    queryFn: () => api.get(`/api/mandates/${id}`).then(r => r.data.data),
    enabled: !!id,
  })

  const { data: pipelineData, isLoading: pipelineLoading } = useQuery({
    queryKey: ['pipeline', id],
    queryFn: () => api.get(`/api/mandates/${id}/pipeline`).then(r => r.data.data ?? []),
    enabled: !!id,
  })

  const { data: interactionsData, isLoading: interactionsLoading } = useQuery({
    queryKey: ['interactions', { mandate_id: id }],
    queryFn: () => api.get('/api/interactions', { params: { mandate_id: id } }).then(r => r.data.data ?? []),
    enabled: !!id,
  })

  const mandate     = mandateData     ?? null
  const pipeline    = pipelineData    ?? []
  const interactions = interactionsData ?? []

  // ── Loading ──────────────────────────────────────────────────────────
  if (mandateLoading) {
    return (
      <div className="page">
        <Link to="/mandates" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          ← BD Pipeline
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <div className="spinner spinner-lg" />
        </div>
      </div>
    )
  }

  // ── Error / not found ────────────────────────────────────────────────
  if (mandateError || !mandate) {
    return (
      <div className="page">
        <Link to="/mandates" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          ← BD Pipeline
        </Link>
        <div style={{
          marginTop: 24, background: 'var(--danger-muted)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 8, padding: '20px 24px', color: '#f87171', fontSize: 14,
        }}>
          {mandateError?.response?.status === 404
            ? 'Mandate not found.'
            : `Error: ${mandateError?.message ?? 'Could not load mandate.'}`}
        </div>
      </div>
    )
  }

  return (
    <div className="page">

      {/* Back link */}
      <div style={{ marginBottom: 16 }}>
        <Link to="/mandates" style={{
          fontSize: 13, color: 'var(--text-secondary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          ← BD Pipeline
        </Link>
      </div>

      {/* ── Section 1: Mandate header ──────────────────────────────── */}
      <MandateHeader mandate={mandate} />

      {/* ── Section 2: Documents ───────────────────────────────────── */}
      <DocumentsSection mandateId={id} />

      {/* ── Sections 3 + 4: Pipeline + Activity (2-col) ───────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

        {/* Investor pipeline */}
        <div>
          {pipelineLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <div className="spinner" />
            </div>
          ) : (
            <PipelineKanban
              entries={pipeline}
              mandateId={id}
              mandate={mandate}
            />
          )}
        </div>

        {/* Activity feed */}
        <ActivityFeed
          interactions={interactions}
          isLoading={interactionsLoading}
          mandateId={id}
        />

      </div>
    </div>
  )
}
